import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { getToken, setToken, TOKEN_KEY, SESSION_ENDED_EVENT } from "../api/client";
import { isCallGuardActive, subscribeCallGuard } from "../services/callGuard";
import {
  login as apiLogin,
  loginAgent as apiLoginAgent,
  loginTwoFactor as apiLoginTwoFactor,
  logout as apiLogout,
  register as apiRegister,
  selectAgentSeat as apiSelectAgentSeat,
  type LoginResponse,
  type RegisterRequest,
  type SeatChoice,
} from "../api/auth";

export type Principal = "operator" | "agent";

interface AuthUser {
  // Agents authenticate with no `"user"` row behind them (Task 1.1's
  // create_jwt deliberately omits user_id from an agent token) — so this is
  // null for an agent session, never a placeholder.
  user_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  principal: Principal;
  // The seat this session is bound to. Always non-null for an agent (baked
  // into the token at sign-in); always null for an operator, who has no
  // single "own seat".
  extension_id: number | null;
  display_name: string | null;
}

interface SignInResult {
  requires2fa: boolean;
  challenge?: string;
  // Populated when an unbound agent must pick a seat before a token is
  // issued (see selectSeat below). Never set for an operator sign-in.
  requiresSeat?: boolean;
  seatChallenge?: string;
  seats?: SeatChoice[];
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  // Set the instant a session ends for real — this tab's own token was
  // invalidated (see client.ts's SESSION_ENDED_EVENT), or another tab
  // cleared the shared token (native `storage` event). Records which
  // principal that session belonged to so a caller can react even after
  // `user` has already gone back to null; cleared on the next successful
  // sign-in or explicit sign-out.
  sessionEndedPrincipal: Principal | null;
  // beads F1: true from the instant a real session-end is DETECTED for an
  // agent who still has a live call up (Softphone.hasActiveSession(), via
  // callGuard.ts), until that call actually ends. The sign-out itself
  // (clearing `user`, flipping `isAuthenticated` false) is deliberately
  // DEFERRED for the whole time this is true — see the effect below — so
  // AgentPanelRoute never has a reason to unmount AgentConsole mid-call; it
  // shows this as a non-blocking banner instead.
  sessionEndingWhileOnCall: boolean;
  signIn: (username: string, password: string) => Promise<SignInResult>;
  signInAgent: (username: string, password: string) => Promise<SignInResult>;
  selectSeat: (seatChallenge: string, extensionId: number) => Promise<void>;
  verifyTwoFactor: (challenge: string, code: string) => Promise<void>;
  signUp: (body: RegisterRequest) => Promise<void>;
  signOut: () => Promise<void>;
}

const USER_KEY = "ictb_user";

function loadStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthState | undefined>(undefined);

// Shape both applySession() overloads normalize to before writing to
// localStorage/state. The seat living in the JWT is authoritative; this
// localStorage copy exists only to render the header/panel before the first
// GET /api/agent/me or GET /api/users/me/permissions returns. Never send this
// copy back to the server as an extension_id parameter — the API ignores it
// for agents anyway (`_require_own_extension`'s agent branch resolves the
// seat from the token, not from client input), and treating a user-editable
// value as truth would put it on a security path.
interface SessionInput {
  token: string | null;
  user_id?: number | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  principal: Principal;
  extension_id?: number | null;
  display_name?: string | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() =>
    getToken() ? loadStoredUser() : null,
  );
  const [sessionEndedPrincipal, setSessionEndedPrincipal] = useState<Principal | null>(null);
  const [sessionEndingWhileOnCall, setSessionEndingWhileOnCall] = useState(false);

  // Effect handlers below fire after `user` may already be stale by the time
  // they run; a ref lets them read the principal that's ending right now.
  const userRef = useRef<AuthUser | null>(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // The principal a deferred sign-out (see below) is waiting to apply to.
  // Non-null only while `sessionEndingWhileOnCall` is true.
  const pendingEndPrincipalRef = useRef<Principal | null>(null);

  // A session ends for real in exactly two ways, both driven from OUTSIDE
  // this component's own signIn/signOut calls:
  //   1. This tab's own token was invalidated — client.ts dispatches
  //      SESSION_ENDED_EVENT only when the failing request carried the
  //      token this tab currently holds, so this never fires for a
  //      stale/foreign token dying in some other tab.
  //   2. Another tab cleared the shared token — the native `storage` event,
  //      which the DOM only fires in OTHER tabs, never the one that made
  //      the change. Because #1 already scopes real clears to genuine
  //      invalidations, this stays coherent without resurrecting the old
  //      wipe-everything bug: a stale tab's dead token no longer clears
  //      storage at all, so it never reaches here in any tab.
  //
  // beads F1: for an AGENT whose console currently reports a live call
  // (callGuard.ts, mirroring Softphone.hasActiveSession()), the actual
  // sign-out is deferred rather than applied immediately — clearing `user`
  // here would flip `isAuthenticated` false, which is exactly what makes
  // AgentPanelRoute unmount AgentConsole (and, with it, tear down the call).
  // `sessionEndingWhileOnCall` lets the route show a banner instead, and the
  // deferred sign-out finally applies itself the instant the call guard
  // reports the call is over (`onCallGuardChange` below) — never sooner,
  // and never later than that.
  useEffect(() => {
    function finishEndSession(principal: Principal | null) {
      setSessionEndingWhileOnCall(false);
      pendingEndPrincipalRef.current = null;
      setSessionEndedPrincipal(principal);
      localStorage.removeItem(USER_KEY);
      setUser(null);
    }

    function endSession() {
      const principal = userRef.current?.principal ?? null;
      // Only an agent session can have a live call behind it — Softphone
      // and callGuard only ever exist on the agent console, never for an
      // operator — so an operator session-end is never deferred.
      if (principal === "agent" && isCallGuardActive()) {
        pendingEndPrincipalRef.current = principal;
        setSessionEndingWhileOnCall(true);
        return;
      }
      finishEndSession(principal);
    }

    function onSessionEnded() {
      endSession();
    }
    function onStorage(e: StorageEvent) {
      if (e.key === TOKEN_KEY && e.newValue === null) {
        endSession();
      }
    }
    // Finalizes a deferred sign-out the moment the call guard flips back to
    // inactive. Subscribed unconditionally (cheap — one listener) rather
    // than only once a deferral is pending, so there's no race between
    // "the pending flag gets set" and "subscribing to hear it clear".
    function onCallGuardChange() {
      if (pendingEndPrincipalRef.current !== null && !isCallGuardActive()) {
        finishEndSession(pendingEndPrincipalRef.current);
      }
    }
    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded);
    window.addEventListener("storage", onStorage);
    const unsubscribeCallGuard = subscribeCallGuard(onCallGuardChange);
    return () => {
      window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded);
      window.removeEventListener("storage", onStorage);
      unsubscribeCallGuard();
    };
  }, []);

  function applySession(session: SessionInput): void {
    setToken(session.token ?? null);
    const u: AuthUser = {
      user_id: session.user_id ?? null,
      first_name: session.first_name ?? null,
      last_name: session.last_name ?? null,
      email: session.email ?? null,
      principal: session.principal,
      extension_id: session.extension_id ?? null,
      display_name: session.display_name ?? null,
    };
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setUser(u);
    // A fresh sign-in supersedes any stale "your session ended" banner —
    // including a deferred one, since there is no longer a pending end to
    // apply against this brand new session.
    setSessionEndedPrincipal(null);
    setSessionEndingWhileOnCall(false);
    pendingEndPrincipalRef.current = null;
  }

  async function signIn(username: string, password: string): Promise<SignInResult> {
    const resp: LoginResponse = await apiLogin({ login_user: username, login_pass: password });
    if (resp.requires_2fa) {
      return { requires2fa: true, challenge: resp.challenge ?? undefined };
    }
    applySession({ ...resp, principal: "operator", extension_id: null, display_name: null });
    return { requires2fa: false };
  }

  async function verifyTwoFactor(challenge: string, code: string): Promise<void> {
    const resp = await apiLoginTwoFactor(challenge, code);
    applySession({ ...resp, principal: "operator", extension_id: null, display_name: null });
  }

  // Agent sign-in — a SEPARATE endpoint from the operator signIn() above
  // (Resolved Decisions #2: agents never use the operator /login form).
  // A bound agent gets a token immediately; an unbound one gets a seat
  // challenge, surfaced here for the /agent-login page's seat-picker step.
  async function signInAgent(username: string, password: string): Promise<SignInResult> {
    const resp = await apiLoginAgent({ login_user: username, login_pass: password });
    if (resp.requires_seat) {
      return {
        requires2fa: false,
        requiresSeat: true,
        seatChallenge: resp.seat_challenge ?? undefined,
        seats: resp.seats,
      };
    }
    applySession({
      token: resp.token,
      principal: "agent",
      extension_id: resp.extension_id,
      display_name: resp.display_name,
    });
    return { requires2fa: false };
  }

  // Second half of an unbound agent's sign-in: exchange the seat challenge
  // (still live even after a 409 from a different seat pick — the backend
  // does not burn it on a claimed-seat conflict) and a chosen seat for a
  // real token.
  async function selectSeat(seatChallenge: string, extensionId: number): Promise<void> {
    const resp = await apiSelectAgentSeat(seatChallenge, extensionId);
    applySession({
      token: resp.token,
      principal: "agent",
      extension_id: resp.extension_id,
      display_name: resp.display_name,
    });
  }

  async function signUp(body: RegisterRequest): Promise<void> {
    const resp = await apiRegister(body);
    applySession({ ...resp, principal: "operator", extension_id: null, display_name: null });
  }

  async function signOut(): Promise<void> {
    // POST /auth/logout is operator-only (CurrentUser) and 403s an agent
    // token — harmless (agent tokens are short-lived and epoch-checked
    // instead, per Resolved Decisions #3), but there's no reason to fire a
    // request that can only ever fail.
    if (user?.principal !== "agent") {
      await apiLogout();
    }
    setToken(null);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    // Explicit, user-initiated — not a surprise, so no banner needed.
    setSessionEndedPrincipal(null);
    setSessionEndingWhileOnCall(false);
    pendingEndPrincipalRef.current = null;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        sessionEndedPrincipal,
        sessionEndingWhileOnCall,
        signIn,
        signInAgent,
        selectSeat,
        verifyTwoFactor,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
